<template>
  <div style="margin-top:2em;">
    <CompareComponent
        title="Overlijdensrisicoverzekering vergelijken"
        compare-type="death"
        :route-params="{policy_id: policyId}"
        :attribute-config="attributeConfig"
        @mounted="setInsuredOne"
        :update-overrides-before-compare="updateInsuredOne"
        show-result-table
        :table-headers="[
            {
              text: 'Logo',
              value: 'logo'
            },
            {
              text: 'Aanbieder',
              value: 'commercialName'
            },
            {
              text: 'Huidige premie per maand',
              value: 'currentPremiumAmount'
            },
            {
              text: 'Premie per maand',
              value: 'premiumAmount'
            },
            {
              text: 'Besparing',
              value: 'besparing'
            },
        ]"
        result-key-for-table="premiums"
        sort-result-by="premiumAmount"
        sort-direction="desc"
        error-description-key="description"
    >
      <template
          v-slot:input="{calculationValues, overrides, getRequiredFields,getNotEditableFields, getOverridesValue, editOverridesValue, getPrettyName, getType, getErrors, getEnumOptions, getSimplesFromCollection}"
      >
        <EditableValuesList
            :values="getSimplesFromCollection(calculationValues)"
            :edit-overrides-value="editOverridesValue"
            :get-overrides-value="getOverridesValue"
            :required-fields="getRequiredFields(calculationValues)"
            :get-errors="getErrors"
            :attribute-config="attributeConfig"
            :not-editable-fields="getNotEditableFields(calculationValues)"
        />
        <table class="table table-fixed table-striped table-bordered">
          <tbody>
          <tr v-if="calculationValues.hasOwnProperty('applicant') && calculationValues.applicant.hasOwnProperty('coverage')">
            <td>Dekking {{ getPrettyName('height') }}</td>
            <td>
              <EditableValue
                  :required="getRequiredFields(calculationValues.applicant.coverage).includes('height')"
                  :value="getOverridesValue('applicant.coverage.height')"
                  @change="editOverridesValue($event,'applicant.coverage.height')"
                  :default-value="calculationValues.applicant.coverage.height"
                  :pretty-name="getPrettyName('height')"
                  :type="getType('height')"
                  :errors="getErrors('applicant.coverage.height')"
              />
            </td>
          </tr>
          <tr v-if="calculationValues.hasOwnProperty('applicant') && calculationValues.applicant.hasOwnProperty('coverage')">
            <td>Dekking {{ getPrettyName('end_date') }}</td>
            <td>
              <EditableValue
                  :required="getRequiredFields(calculationValues.applicant.coverage).includes('end_date')"
                  :value="getOverridesValue('applicant.coverage.end_date')"
                  @change="editOverridesValue($event,'applicant.coverage.end_date')"
                  :default-value="calculationValues.applicant.coverage.end_date"
                  :pretty-name="getPrettyName('end_date')"
                  :type="getType('end_date')"
                  :errors="getErrors('applicant.coverage.end_date')"
              />
            </td>
          </tr>
          <tr
              v-if="hasBoth"
          >
            <td>Verzekerde</td>
            <td>
              <select class="form-control" v-model="insuredOne">
                <option value="applicant">Hoofdklant</option>
                <option value="partner">Partner</option>
                <option value="both">Beide</option>
              </select>
            </td>
          </tr>
          </tbody>
        </table>
        <a-row style="margin-top:1em;">
          <a-col :span="insuredOne === 'both' ? 12 : 24"
                 v-show="insuredOne === 'applicant' || insuredOne === 'both'"
          >
            <EditableValuesList
                :values="getSimplesFromCollection(calculationValues['applicant'])"
                :edit-overrides-value="editOverridesValue"
                :get-overrides-value="getOverridesValue"
                :required-fields="getRequiredFields(calculationValues['applicant'])"
                :get-errors="getErrors"
                :attribute-config="attributeConfig"
                attribute-pointer-prefix="applicant"
                :title="getPrettyName('applicant')"
                :not-editable-fields="getNotEditableFields(calculationValues)"
            />
          </a-col>
          <a-col :span="insuredOne === 'both' ? 12 : 24"
                 v-show="insuredOne === 'partner' || insuredOne === 'both'"
          >
            <EditableValuesList
                :values="getSimplesFromCollection(calculationValues['partner'])"
                :edit-overrides-value="editOverridesValue"
                :get-overrides-value="getOverridesValue"
                :required-fields="getRequiredFields(calculationValues['partner'])"
                :get-errors="getErrors"
                :attribute-config="attributeConfig"
                attribute-pointer-prefix="partner"
                :title="getPrettyName('partner')"
                :not-editable-fields="getNotEditableFields(calculationValues)"
            />
          </a-col>
        </a-row>
      </template>
      <template v-slot:table.logo="{option}">
        <lazy-component>
          <object :data="option.imagePath" :alt='`Foto ${option.provider}`' style="max-height:50px; max-width:100px">
            Geen afbeelding gevonden
          </object>
        </lazy-component>
      </template>
      <template v-slot:table.premiumAmount="{value}">
        {{ value | toCurrency }}
      </template>
      <template v-slot:table.currentPremiumAmount="{result}">
        <span v-if="result.currentPremiumAmount">
          {{ result.currentPremiumAmount | toCurrency }}
        </span>
        <span v-else>
          n.v.t.
        </span>
      </template>
      <template v-slot:table.besparing="{option, result}">
        <span :class="(result.currentPremiumAmount - option.premiumAmount) < 0 ? 'text-danger' : 'text-success'">
          {{ (result.currentPremiumAmount - option.premiumAmount) | toCurrency }}
        </span>
      </template>
    </CompareComponent>
  </div>
</template>

<script>
import EditableValue from "./EditableValue";
import EditableValuesList from './EditableValuesList';
import CompareComponent from "./CompareComponent";

export default {
  name: 'DeathInsurance',
  props: {
    customerId: [String, Number],
    policyId: [String, Number]
  },
  components: {
    EditableValue,
    CompareComponent,
    EditableValuesList
  },
  data() {
    return {
      attributeConfig: {
        applicant: {
          prettyName: 'Verzekerde hoofdklant',
        },
        partner: {
          prettyName: 'Verzekerde partner',
        },
        is_smoking: {
          prettyName: 'Rookt',
          type: 'boolean'
        },
        gender: {
          prettyName: 'Geslacht',
          type: 'enum',
          enumOptions: {
            Male: 'man',
            Female: 'vrouw'
          }
        },
        date_of_birth: {
          prettyName: 'Geboortedatum',
          type: 'date'
        },
        coverage: {
          prettyName: 'Dekking',
          type: 'euro'
        },
        height: {
          prettyName: 'Verzekerd bedrag',
          type: 'euro'
        },
        end_date: {
          prettyName: 'Einddatum',
          type: 'date'
        },
        coverage_course: {
          prettyName: 'Kapitaal verloop',
          type: 'enum',
          enumOptions: {
            Unchanged: 'Gelijkblijvend',
            LinearDescending: 'Lineair dalend',
            AnnuityDescending: 'Annuitair dalend'
          }
        },
        payment_frequency: {
          prettyName: 'Betaalperiode',
          type: 'enum',
          enumOptions: {
            Monthly: 'maandelijks',
            Quarterly: 'per kwartaal',
            HalfYearly: 'per half jaar',
            Yearly: 'jaarlijks'
          }
        },
        start_date: {
          prettyName: 'Startdatum',
          type: 'date'
        },
        annuity_rate: {
          prettyName: 'Rente',
          type: 'percentage'
        },
        product_is_mortgage_related: {
          prettyName: 'Verpanding',
          type: 'boolean'
        },
        is_fixed_fee: {
          prettyName: 'Vaste premie',
          type: 'boolean'
        },
        provider: {
          prettyName: 'Aanbieder'
        },
        commercialName: {
          prettyName: 'Product'
        },
        premiumTotal: {
          prettyName: 'Totaal bedrag'
        },
        premiumAmount: {
          prettyName: 'Premie per maand'
        },
        fixedFee: {
          prettyName: 'Vaste premie'
        },
      },
      insuredOne: null,
      hasBoth: false
    }
  },
  methods: {
    setInsuredOne(calculationValues) {
      if (calculationValues.hasOwnProperty('partner') && calculationValues.partner != null && calculationValues.hasOwnProperty('applicant') && calculationValues.applicant != null) {
        this.hasBoth = true;
        this.insuredOne = 'both';
      } else if (calculationValues.hasOwnProperty('partner') && calculationValues.partner != null) {
        this.insuredOne = 'partner';
        this.hasBoth = false;
      } else {
        this.insuredOne = 'applicant';
        this.hasBoth = false;
      }
    },
    updateInsuredOne(overrides, calculationValues) {
      if (this.insuredOne === 'applicant') {
        overrides['partner'] = {};
      } else if (this.insuredOne === 'partner') {
        overrides['applicant'] = _.merge(overrides['partner'], calculationValues['partner'] || null);
        overrides['partner'] = {};
      } else {
        if (!overrides.hasOwnProperty('partner')) {
          overrides['partner'] = {};
        }

        let calculationValue = calculationValues.hasOwnProperty('applicant') ? calculationValues['applicant']['coverage'] || {} : {};
        let override = overrides.hasOwnProperty('applicant') ? overrides['applicant']['coverage'] || {} : {};

        overrides['partner']['coverage'] = _.merge(calculationValue,override);
      }

      return overrides;
    }
  }
}
</script>

<style scoped>

</style>
